import { KeyboardArrowDownRounded, PublicRounded } from '@mui/icons-material';
import { Box, Menu, Typography } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { NEW_FEATURE_ALERTS } from '@/constants';
import { useNewFeatureAlert, useRegionPreferenceManagement } from '@/hooks';
import { EVENT_CONSTANTS } from '@/services';
import { selectSessionUserId } from '@/store';
import { appStyles } from '@/theme';

import { NewFeatureTooltip } from '../NewFeatureTooltip';
import { RegionSwitchMenuContents } from './RegionSwitchMenuContents';
import { getSortedRegions, RegionShortLabel } from './utils';

const ALERT_ID = NEW_FEATURE_ALERTS.CONTENT_REGION_PREFERENCE;

export const RegionSwitch = () => {
  const anchor = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  const { selectedRegions } = useRegionPreferenceManagement();

  const userId = useSelector(selectSessionUserId);

  const selectedRegionLabels = useMemo(() => {
    if (!selectedRegions) return '';

    const sortedRegions = getSortedRegions(selectedRegions);

    if (sortedRegions.length === 0) return '';
    if (sortedRegions.length < 3)
      return sortedRegions
        .map(
          region => RegionShortLabel[region as keyof typeof RegionShortLabel],
        )
        .join(', ');

    return `${RegionShortLabel[sortedRegions[0] as keyof typeof RegionShortLabel]} +${sortedRegions.length - 1}`;
  }, [selectedRegions]);

  const newFeatureAlert = useNewFeatureAlert(ALERT_ID, { isEnabled: !!userId });

  if (!userId) return null;

  return (
    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
      <NewFeatureTooltip
        id={ALERT_ID}
        featureName="Set your episode region preference"
        featureDescription="Select the regions you work in and we'll tailor your library to the standards and regulations that apply to you."
        placement="bottom-end"
        isVisible={newFeatureAlert.isVisible}
        onDismiss={newFeatureAlert.dismiss}>
        <Box
          ref={anchor}
          role="button"
          aria-controls="region-preference"
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          sx={{
            height: 48,
            borderRadius: 6,
            px: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            cursor: 'pointer',
            borderColor: 'grey200',
            '&:hover': {
              borderColor: 'secondary.main',
            },
          }}
          onClick={() => setIsOpen(true)}>
          <PublicRounded sx={{ color: 'secondary.main' }} />
          {!!selectedRegionLabels && (
            <Typography variant="body2SemiBold" sx={{ color: 'white', ml: 1 }}>
              {selectedRegionLabels}
            </Typography>
          )}
          <KeyboardArrowDownRounded sx={{ color: 'grey100', ml: 0.5 }} />
        </Box>
      </NewFeatureTooltip>
      <Menu
        id="region-preference"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        anchorEl={anchor.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ mt: 0.5 }}
        slotProps={{
          paper: {
            sx: {
              ...appStyles.popoverPaper,
              width: 300,
            },
          },
          list: { sx: { p: 0 } },
        }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">Episode Region Preferences</Typography>
          <RegionSwitchMenuContents
            onClose={() => setIsOpen(false)}
            location={{ component: EVENT_CONSTANTS.COMPONENT.NAV_BAR }}
          />
        </Box>
      </Menu>
    </Box>
  );
};
